import { useQueries } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { SystemEntity, EntityAssociationType } from '@erp/shared-types';
import type { EntityAssociationItem } from '@erp/rpc';
import { isProposedSplitLink } from './relatedLinesSummary';

/**
 * Invoice line item id → its AP_INVOICE_MATCHING associations. Fetched upfront
 * with one query per line so tables avoid per-row N+1 fetches.
 *
 * `withoutProposedSplitLinks` leaves out the tentative links of a split
 * suggestion: a screen that shows one match per line must not mistake the
 * first of them for the line's match.
 */
export function useLineItemAssociationsMap(
  lineItems: { id: string }[],
  options?: { withoutProposedSplitLinks?: boolean },
) {
  const queryAssociations = useQueries({
    queries: lineItems.map((lineItem) =>
      orpc.core.entityAssociations.list.queryOptions({
        input: {
          entityId: lineItem.id,
          entityType: SystemEntity.AP_INVOICE_LINE_ITEM,
          associationType: EntityAssociationType.AP_INVOICE_MATCHING,
        },
      }),
    ),
    combine: (results) =>
      results.map((result) => result.data?.associations ?? []),
  });

  const map = new Map<string, EntityAssociationItem[]>();
  lineItems.forEach((lineItem, index) => {
    const associations = queryAssociations[index] ?? [];
    map.set(
      lineItem.id,
      options?.withoutProposedSplitLinks
        ? associations.filter((item) => !isProposedSplitLink(item))
        : associations,
    );
  });
  return map;
}
